"use client";
import { hamtaSessionSvar } from "@/lib/klient-session";
// Site/src/components/mobile/MobileQrButton.tsx
import { MOBIL_PAR_CURRENT_MS } from "@/lib/klient-intervall";
import { useEffect, useState } from "react";
import MobileQrModal from "./MobileQrModal";
import { useModal } from "@/components/ui/ModalProvider";
import { harBevakare, parkopplingUrSvar, sattParkoppling, useParkopplingLage } from "@/lib/parkoppling-store";

interface SessionUser {
  role: string;
  impersonating?: { studentId: string };
}

export default function MobileQrButton({
  forbjudenIProv = false,
  dold = false,
}: {
  /** Aktivt prov som förbjuder mobilparning: klick ger förbjudet-popup. */
  forbjudenIProv?: boolean;
  /** Eleven sitter på en telefon: knappen syns inte, men pollningen av
   *  parkopplingsstatus löper som vanligt. */
  dold?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  // The shared state (also written by the QR modal), so the icon turns green the moment it sees a pairing.
  const paired = useParkopplingLage().active;
  const { notify } = useModal();

  useEffect(() => {
    let cancelled = false;
    hamtaSessionSvar<{ user?: SessionUser }>()
      .then((d) => {
        if (cancelled) return;
        setUser(d?.user ?? null);
        setSessionLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setSessionLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pairing state: read at mount and whenever the tab shows; polled every 30 s only while a component that
  // shows photo buttons is mounted (harBevakare, lib/parkoppling-store.ts). Until 2026-10-31 every student page
  // polled it every 30 s all day (one statement each) for an icon colour that is not time-critical. Pausar i
  // dold flik (2026-10-22: en glömd flik pollade hela natten).
  useEffect(() => {
    if (!sessionLoaded || !user) return;
    if (user.role === "larare" && !user.impersonating) return;
    let cancelled = false;
    async function tick() {
      try {
        const res = await fetch("/api/mobile-pair/current", {
          cache: "no-store",
        });
        if (cancelled || !res.ok) return;
        const data = (await res.json()) as { active?: boolean; enhet?: unknown };
        sattParkoppling(parkopplingUrSvar(data));
      } catch {
        // Tyst — försök igen vid nästa intervall
      }
    }
    tick();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible" && harBevakare()) void tick();
    }, MOBIL_PAR_CURRENT_MS);
    const vidSynlig = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", vidSynlig);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", vidSynlig);
    };
  }, [sessionLoaded, user]);

  // Lärare i normalt läge ska inte se ikonen alls. Lärare som impersonerar en
  // elev ser den (som elev gör), men klick → "Ej tillgängligt"-popup eftersom
  // mobil-pairing skulle skapas mot elevens userId felaktigt.
  if (!sessionLoaded || !user) return null;
  const isLarare = user.role === "larare";
  if (isLarare && !user.impersonating) return null;
  const isImpersonating = !!user.impersonating;

  async function handleClick() {
    if (forbjudenIProv) {
      await notify({
        title: "Inte tillåtet under provet",
        message:
          "Läraren har stängt av mobil och läsplatta för det här provet. Fota din lösning med datorns kamera i stället.",
      });
      return;
    }
    if (isImpersonating) {
      await notify({
        title: "Ej tillgängligt för lärare",
        message:
          "Mobil-pairing och foto-skanning fungerar bara för elever i sin egen session.",
      });
      return;
    }
    setOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        aria-label={paired ? "Mobil eller läsplatta kopplad" : "Koppla mobil eller läsplatta"}
        title={
          paired
            ? "Mobilen eller läsplattan är kopplad — klicka för att hantera"
            : "Koppla din mobil (räknare + kamera) eller läsplatta (rityta) till datorn"
        }
        className={`p-2 rounded-md hover:bg-[var(--color-nav-border)] transition-colors${dold ? " hidden" : ""}`}
        style={{
          color: paired
            ? "var(--color-figur-mossa)"
            : "var(--color-nav-text)",
        }}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.75}
        >
          <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
      </button>
      <MobileQrModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
